import { goAnchor } from "./app-nav";
import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ShieldCheck, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { conversationHints, conversationUnits, conversationsNav, conversationsNavAnchors } from "@/mocks/conversations";
import { isEn, t, tn } from "@/lib/i18n";
import { inboxRulesApi, type InboxRules, type RuleCategory } from "./inbox-rules-api";
import { apiErrorText } from "@/lib/api";

/* Окно «Диалоги» на API правил (`/settings/inbox-rules`): автозакрытие по неактивности, заметка и сообщение
   при закрытии, категории, итог и тональность от ИИ. Правят владелец и администраторы, остальные видят. */

type Category = RuleCategory;
const INPUT =
  "h-9 w-full box-border min-w-0 rounded-btn border border-border bg-background px-2.5 text-[12.5px] font-medium text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

const plural = (n: number, unit: (typeof conversationUnits)[number]) => {
  if (isEn) return Math.abs(n) === 1 ? unit.one : unit.label;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return unit.one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return unit.few;
  return unit.label;
};

function Switch({ checked, label, onChange }: { checked: boolean; label: string; onChange: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={cn("relative mt-0.5 h-6 w-11 flex-none cursor-pointer rounded-full transition", checked ? "bg-primary" : "bg-foreground/20")}
    >
      <span
        className={cn("absolute top-0.5 size-5 rounded-full bg-white transition-all", checked ? "start-[1.375rem]" : "start-0.5")}
        aria-hidden
      />
    </button>
  );
}

function ConversationsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [autoClose, setAutoClose] = useState(true);
  const [closeValue, setCloseValue] = useState("1");
  const [closeUnit, setCloseUnit] = useState("days");
  const [autoNotes, setAutoNotes] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sentiment, setSentiment] = useState(true);
  const [advanced, setAdvanced] = useState(false);
  const [keepUnread, setKeepUnread] = useState(true);
  const [closeMessage, setCloseMessage] = useState(false);
  const [closeMessageText, setCloseMessageText] = useState("");
  const [requireNote, setRequireNote] = useState(false);
  const [remind, setRemind] = useState("8");
  const [canEdit, setCanEdit] = useState(false);
  const [aiReady, setAiReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [manage, setManage] = useState(false);
  const [manageBackup, setManageBackup] = useState<Category[] | null>(null);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4200);
  };

  const draftState = { autoClose, closeValue, closeUnit, autoNotes, categories, sentiment, keepUnread, closeMessage, closeMessageText, requireNote, remind };
  const [saved, setSaved] = useState(() => JSON.stringify(draftState));
  const dirty = JSON.stringify(draftState) !== saved;

  const applyRules = (r: InboxRules) => {
    setAutoClose(r.autoClose);
    setCloseValue(String(r.closeAfter));
    setCloseUnit(r.closeUnit);
    setAutoNotes(r.autoNotes);
    setCategories(r.categories.map((c) => ({ ...c })));
    setSentiment(r.sentiment);
    setKeepUnread(r.keepUnread);
    setCloseMessage(r.closeMessage);
    setCloseMessageText(r.closeMessageText);
    setRequireNote(r.requireNote);
    setRemind(String(r.remindHours));
    setCanEdit(r.canEdit);
    setAiReady(r.ai);
    setSaved(
      JSON.stringify({
        autoClose: r.autoClose,
        closeValue: String(r.closeAfter),
        closeUnit: r.closeUnit,
        autoNotes: r.autoNotes,
        categories: r.categories.map((c) => ({ ...c })),
        sentiment: r.sentiment,
        keepUnread: r.keepUnread,
        closeMessage: r.closeMessage,
        closeMessageText: r.closeMessageText,
        requireNote: r.requireNote,
        remind: String(r.remindHours),
      }),
    );
  };

  useEffect(() => {
    inboxRulesApi.get().then(applyRules, (err) => flash(apiErrorText(err)));
  }, []);

  const unit = conversationUnits.find((u) => u.id === closeUnit) ?? conversationUnits[1];
  const amount = Number(closeValue);
  const amountOk = Number.isFinite(amount) && amount >= 1 && amount <= 365;

  const setCategory = (id: string, data: Partial<Category>) =>
    setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, ...data } : c)));

  // открываем управление категориями с запоминанием текущего списка: «Отмена» вернёт его
  const openManage = () => {
    setManageBackup(categories.map((c) => ({ ...c })));
    setManage(true);
  };

  const closeManage = () => {
    if (manageBackup) setCategories(manageBackup.map((c) => ({ ...c })));
    setManage(false);
    setManageBackup(null);
  };

  const addCategory = () => {
    const next: Category = {
      id: `c-${Date.now()}`,
      name: t("Категория {n}", { n: categories.length + 1 }),
      description: "",
      color: "primary",
    };
    setCategories((prev) => [...prev, next]);
  };

  const removeCategory = (c: Category) => {
    if (categories.length <= 1) {
      flash(t("Последнюю категорию удалить нельзя — в списке должна остаться хотя бы одна"));
      return;
    }
    setCategories((prev) => prev.filter((x) => x.id !== c.id));
    flash(t("Категория «{name}» удалена — диалоги из неё получат «Другое»", { name: c.name || t("без названия") }));
  };

  const save = async () => {
    if (!canEdit || busy) return;
    const clean = categories.map((c) => ({ ...c, name: c.name.trim() || t("Без названия") }));
    setBusy(true);
    try {
      const r = await inboxRulesApi.update({
        autoClose,
        closeAfter: amount,
        closeUnit: closeUnit as InboxRules["closeUnit"],
        autoNotes,
        categories: clean,
        sentiment,
        keepUnread,
        closeMessage,
        closeMessageText: closeMessageText.trim() || undefined,
        requireNote,
        remindHours: Number(remind),
      });
      applyRules(r);
      setManage(false);
      setManageBackup(null);
      flash(t("Настройки диалогов сохранены — новые правила начнут работать со следующего диалога"));
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    const back = JSON.parse(saved) as typeof draftState;
    setAutoClose(back.autoClose);
    setCloseValue(back.closeValue);
    setCloseUnit(back.closeUnit);
    setAutoNotes(back.autoNotes);
    setCategories(back.categories.map((c) => ({ ...c })));
    setSentiment(back.sentiment);
    setKeepUnread(back.keepUnread);
    setCloseMessage(back.closeMessage);
    setCloseMessageText(back.closeMessageText);
    setRequireNote(back.requireNote);
    setRemind(back.remind);
    setManage(false);
    setManageBackup(null);
    flash(t("Изменения отменены — настройки вернулись к сохранённому виду"));
  };

  const onNav = (id: string) => {
    if (id === "conversations") return;
    const anchor = conversationsNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("В демо открыт только раздел «Диалоги» — остальные настройки не меняются"));
  };

  return (
    <Band id="conversations" tone="default">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("настройки → входящие → диалоги")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("Категорий: {n}", { n: categories.length })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {conversationsNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "conversations";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <ShieldCheck className="size-3.5" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Правила закрытия и категории меняет администратор. Продавцы закрывают диалоги вручную.")}
              </p>
            </div>
          </div>

          {/* ── содержимое ── */}
          <div className="min-w-0 p-3.5 md:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold leading-snug">{t("Диалоги")}</h3>
                <p className="mt-1 max-w-xl text-[12px] leading-relaxed text-muted-foreground">
                  {t("Настройте, как обрабатываются и закрываются диалоги в рабочем пространстве.")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => flash(t("Правила применяются ко всем диалогам: автозакрытие проверяется раз в час, итог ИИ пишется при закрытии, если на сервере есть ключ OpenRouter."))}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[12px] font-semibold text-primary transition hover:opacity-80"
              >
                <i className="ri-book-open-line text-sm leading-none" aria-hidden />
                {t("Узнать больше")}
              </button>
            </div>

            <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
              {conversationsNav.flatMap((g) => g.items).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onNav(it.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    it.id === "conversations" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <i className={it.icon} aria-hidden />
                  {it.label}
                </button>
              ))}
            </div>

            <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_15rem]">
              <div className="flex min-w-0 flex-col gap-3.5">
                {/* автозакрытие */}
                <div className="rounded-card border border-border bg-background/40 p-3.5">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[12.5px] font-semibold">{t("Автозакрытие диалогов")}</p>
                      <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                        {t("Диалог закрывается после периода неактивности. Новое сообщение снова открывает его.")}
                      </p>
                    </div>
                    <Switch
                      checked={autoClose}
                      label={t("Автозакрытие диалогов")}
                      onChange={() => {
                        setAutoClose((v) => !v);
                        flash(autoClose ? t("Автозакрытие выключено — диалоги остаются открытыми до ручного закрытия") : t("Автозакрытие включено — неактивные диалоги будут закрываться автоматически"));
                      }}
                    />
                  </div>

                  <div className={cn("mt-3 flex flex-wrap items-center gap-2 transition", !autoClose && "opacity-45")}>
                    <label className="inline-flex items-center gap-2 text-[12px] text-muted-foreground">
                      {t("Закрывать через")}
                      <input
                        type="number"
                        min={1}
                        max={365}
                        value={closeValue}
                        disabled={!autoClose}
                        onChange={(e) => setCloseValue(e.target.value)}
                        aria-label={t("Через сколько закрывать диалог")}
                        className={cn(INPUT, "w-16 text-center tabular-nums")}
                      />
                    </label>
                    <span className="relative inline-flex items-center">
                      <select
                        value={closeUnit}
                        disabled={!autoClose}
                        onChange={(e) => setCloseUnit(e.target.value)}
                        aria-label={t("Единица времени автозакрытия")}
                        className={cn(INPUT, "cursor-pointer appearance-none ps-2.5 pe-7")}
                      >
                        {conversationUnits.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute end-1.5 size-3.5 text-muted-foreground" aria-hidden />
                    </span>
                    <span className="text-[11.5px] text-muted-foreground">{amountOk ? plural(amount, unit) : ""}</span>
                  </div>
                  {!amountOk ? (
                    <p className="mt-1.5 text-[11px] text-red-500">{t("Укажите целое число от 1 до 365 — иначе диалоги не закроются.")}</p>
                  ) : (
                    <p className="mt-1.5 text-[11px] text-muted-foreground">{t("Сейчас: диалог без ответа закроется через {n} {unit}.", { n: amount, unit: plural(amount, unit) })}</p>
                  )}
                </div>

                {/* авто-заметки */}
                <div className="flex items-start gap-3 rounded-card border border-border bg-background/40 p-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] font-semibold">{t("Автоматические заметки при закрытии")}</p>
                    <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                      {t("При закрытии AI определяет категорию и собирает краткое содержание диалога.")}
                    </p>
                  </div>
                  <Switch
                    checked={autoNotes}
                    label={t("Автоматические заметки при закрытии")}
                    onChange={() => {
                      setAutoNotes((v) => !v);
                      flash(autoNotes ? t("Автозаметки выключены — при закрытии диалога заметка не создаётся") : t("Автозаметки включены — к закрытому диалогу добавится summary"));
                    }}
                  />
                </div>
                {autoNotes && !aiReady ? (
                  <p className="-mt-2 px-1 text-[11px] text-amber-600">{t("Ключ OpenRouter на сервере не задан — итог ИИ пока не пишется.")}</p>
                ) : null}

                {/* обязательная заметка при закрытии */}
                <div className="flex items-start gap-3 rounded-card border border-border bg-background/40 p-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] font-semibold">{t("Заметка при закрытии обязательна")}</p>
                    <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                      {t("Закрывая диалог вручную, продавец пишет, чем всё закончилось. Без заметки закрыть нельзя.")}
                    </p>
                  </div>
                  <Switch checked={requireNote} label={t("Заметка при закрытии обязательна")} onChange={() => setRequireNote((v) => !v)} />
                </div>

                {/* категории */}
                <div className="rounded-card border border-border bg-background/40 p-3.5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[12.5px] font-semibold">{t("Категория диалога")}</p>
                      <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                        {t("AI выбирает самую близкую тему. Понятные названия и описания помогают не путать категории.")}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={openManage}
                      className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 text-[11.5px] font-semibold text-muted-foreground transition hover:text-foreground"
                    >
                      <i className="ri-settings-3-line text-sm leading-none" aria-hidden />
                      {t("Управление")}
                    </button>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {categories.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={openManage}
                        title={c.description || c.name}
                        className={cn(
                          "inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                          c.color === "accent"
                            ? "border-amber-400/45 bg-amber-400/12 text-amber-600 hover:border-amber-400"
                            : c.color === "neutral"
                              ? "border-border bg-muted/60 text-muted-foreground hover:text-foreground"
                              : "border-primary/40 bg-primary/10 text-primary hover:border-primary",
                        )}
                      >
                        <i className="ri-price-tag-3-line text-[12px] leading-none" aria-hidden />
                        {c.name || t("Без названия")}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    {tn(categories.length, "категория", "категории", "категорий")} · {t("нажмите на тег, чтобы изменить название и описание")}
                  </p>
                </div>

                {/* тональность */}
                <div className="flex items-start gap-3 rounded-card border border-border bg-background/40 p-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] font-semibold">{t("Тональность диалога")}</p>
                    <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                      {t("При закрытии AI проверяет сообщения клиента и помечает тон — положительный, нейтральный или отрицательный — с объяснением в одну строку.")}
                    </p>
                  </div>
                  <Switch
                    checked={sentiment}
                    label={t("Тональность диалога")}
                    onChange={() => {
                      setSentiment((v) => !v);
                      flash(sentiment ? t("Оценка тональности выключена — новые диалоги будут без метки") : t("Оценка тональности включена — закрытые диалоги получат метку тона"));
                    }}
                  />
                </div>

                {/* расширенные настройки */}
                <div className="rounded-card border border-border bg-background/40">
                  <button
                    type="button"
                    onClick={() => setAdvanced((v) => !v)}
                    aria-expanded={advanced}
                    className="flex w-full cursor-pointer items-center justify-between gap-3 px-3.5 py-3 text-start"
                  >
                    <span className="text-[12.5px] font-semibold">{t("Расширенные настройки")}</span>
                    <ChevronDown className={cn("size-4 flex-none text-muted-foreground transition", advanced && "rotate-180")} aria-hidden />
                  </button>
                  {advanced ? (
                    <div className="flex flex-col gap-3 border-t border-border/60 px-3.5 py-3.5">
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] font-semibold">{t("Не закрывать диалоги с непрочитанными")}</p>
                          <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                            {t("Если клиент написал, а продавец не открыл диалог, автозакрытие ждёт и не срабатывает.")}
                          </p>
                        </div>
                        <Switch
                          checked={keepUnread}
                          label={t("Не закрывать диалоги с непрочитанными сообщениями")}
                          onChange={() => setKeepUnread((v) => !v)}
                        />
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] font-semibold">{t("Сообщение клиенту при закрытии")}</p>
                          <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                            {t("Отправлять короткое «Спасибо за обращение» перед тем, как диалог закроется.")}
                          </p>
                        </div>
                        <Switch checked={closeMessage} label={t("Сообщение клиенту при закрытии")} onChange={() => setCloseMessage((v) => !v)} />
                      </div>
                      {closeMessage ? (
                        <label className="flex flex-col gap-1">
                          <span className="sr-only">{t("Текст сообщения при закрытии")}</span>
                          <textarea
                            value={closeMessageText}
                            onChange={(e) => setCloseMessageText(e.target.value.slice(0, 1000))}
                            rows={2}
                            placeholder={t("Спасибо за обращение! Если появятся вопросы — напишите, мы на связи.")}
                            className={cn(INPUT, "h-auto min-h-14 resize-y py-2 leading-relaxed")}
                          />
                        </label>
                      ) : null}
                      <label className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3">
                        <span className="min-w-0">
                          <span className="block text-[12px] font-semibold">{t("Напоминание о диалоге без ответа")}</span>
                          <span className="mt-0.5 block text-[11px] text-muted-foreground">{t("Через сколько часов подсвечивать такой диалог в ленте.")}</span>
                        </span>
                        <span className="relative inline-flex items-center">
                          <select
                            value={remind}
                            onChange={(e) => setRemind(e.target.value)}
                            aria-label={t("Напоминание о диалоге без ответа")}
                            className={cn(INPUT, "w-28 cursor-pointer appearance-none ps-2.5 pe-7")}
                          >
                            {["2", "4", "8", "12", "24"].map((h) => (
                              <option key={h} value={h}>
                                {t("{n} ч", { n: h })}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="pointer-events-none absolute end-1.5 size-3.5 text-muted-foreground" aria-hidden />
                        </span>
                      </label>
                    </div>
                  ) : null}
                </div>

                {/* сохранение */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[11px] text-muted-foreground">
                    {!canEdit ? t("Только просмотр: правила меняют владелец и администраторы.") : dirty ? t("Есть несохранённые изменения") : t("Все изменения сохранены")}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={cancel}
                      disabled={!dirty}
                      className="inline-flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-3.5 text-[12px] font-semibold text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {t("Отмена")}
                    </button>
                    <button
                      type="button"
                      onClick={() => void save()}
                      disabled={!dirty || !amountOk || !canEdit || busy}
                      className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Check className="size-3.5" aria-hidden />
                      {t("Сохранить")}
                    </button>
                  </div>
                </div>
              </div>

              {/* подсказки справа */}
              <aside className="hidden flex-col gap-2 xl:flex">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Как это работает")}</p>
                {conversationHints.map((h) => (
                  <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
                    <p className="flex items-start gap-2 text-[11.5px] font-semibold leading-snug">
                      <span className="grid size-6 flex-none place-items-center rounded-btn bg-primary/12 text-[13px] leading-none text-primary" aria-hidden>
                        <i className={h.icon} />
                      </span>
                      <span className="min-w-0">{h.title}</span>
                    </p>
                    <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">{h.text}</p>
                  </div>
                ))}
              </aside>
            </div>

          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {/* ── окно управления категориями ── */}
        {manage ? (
          <div className="absolute inset-0 z-[60] flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="category-dialog-title"
              className="relative max-h-full w-full max-w-xl overflow-y-auto rounded-card border border-border bg-card p-5 shadow-lift md:p-6"
            >
              <button
                type="button"
                onClick={closeManage}
                aria-label={t("Закрыть окно категорий")}
                className="absolute end-3 top-3 grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" aria-hidden />
              </button>

              <div className="flex items-start gap-3 pe-8">
                <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                  <i className="ri-price-tag-3-line" />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Категории диалогов")}</p>
                  <h3 id="category-dialog-title" className="mt-1 text-lg font-semibold leading-snug">
                    {t("Что AI выбирает при закрытии")}
                  </h3>
                </div>
              </div>

              <p className="mt-3 text-[11.5px] leading-relaxed text-muted-foreground">
                {t("Чем точнее описание, тем реже диалоги попадают в «Другое». Изменения применятся после «Сохранить».")}
              </p>

              <div className="mt-4 flex flex-col gap-2.5">
                {categories.map((c) => (
                  <div key={c.id} className="rounded-btn border border-border bg-background/50 p-2.5">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "grid size-7 flex-none place-items-center rounded-btn text-[13px] leading-none",
                          c.color === "accent" ? "bg-amber-400/15 text-amber-600" : c.color === "neutral" ? "bg-muted text-muted-foreground" : "bg-primary/12 text-primary",
                        )}
                        aria-hidden
                      >
                        <i className="ri-price-tag-3-line" />
                      </span>
                      <label className="min-w-0 flex-1">
                        <span className="sr-only">{t("Название категории")}</span>
                        <input
                          value={c.name}
                          maxLength={48}
                          onChange={(e) => setCategory(c.id, { name: e.target.value })}
                          placeholder={t("Название категории")}
                          className={INPUT}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => removeCategory(c)}
                        aria-label={t("Удалить категорию {name}", { name: c.name })}
                        className="grid size-8 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-red-500/10 hover:text-red-500"
                      >
                        <i className="ri-delete-bin-line text-[15px] leading-none" aria-hidden />
                      </button>
                    </div>
                    <label className="mt-2 block">
                      <span className="sr-only">{t("Описание категории")}</span>
                      <input
                        value={c.description}
                        maxLength={140}
                        onChange={(e) => setCategory(c.id, { description: e.target.value })}
                        placeholder={t("Когда выбирать эту категорию")}
                        className={cn(INPUT, "font-normal text-muted-foreground")}
                      />
                    </label>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addCategory}
                className="mt-2.5 inline-flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-dashed border-border text-[12px] font-semibold text-muted-foreground transition hover:border-primary hover:text-primary"
              >
                <i className="ri-add-line" aria-hidden />
                {t("Добавить категорию")}
              </button>

              <p className="mt-3 flex items-start gap-2 rounded-btn border border-border bg-background/60 px-3 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
                <ShieldCheck className="mt-0.5 size-3.5 flex-none text-primary" aria-hidden />
                {t("При удалении категории её диалоги не пропадают — они переходят в «Другое».")}
              </p>

              <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={closeManage}
                  className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[13px] font-semibold text-muted-foreground transition hover:text-foreground"
                >
                  {t("Отмена")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setManage(false);
                    setManageBackup(null);
                    flash(t("Категории обновлены — не забудьте сохранить настройки диалогов"));
                  }}
                  disabled={!dirty}
                  className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Check className="size-4" aria-hidden />
                  {t("Готово")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default ConversationsDemo;